/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AcquirerResponse } from '../models/AcquirerResponse';
import type { BulkGrantResponse } from '../models/BulkGrantResponse';
import type { CampaignResponse } from '../models/CampaignResponse';
import type { CompanyExternalIdResponse } from '../models/CompanyExternalIdResponse';
import type { CompanyResponse } from '../models/CompanyResponse';
import type { DownloadFileTypeResponse } from '../models/DownloadFileTypeResponse';
import type { GiftAutocompleteResponse } from '../models/GiftAutocompleteResponse';
import type { GiftDetailAutocompleteResponse } from '../models/GiftDetailAutocompleteResponse';
import type { GiftSetAutocompleteResponse } from '../models/GiftSetAutocompleteResponse';
import type { IssuerResponse } from '../models/IssuerResponse';
import type { ShopExternalIdResponse } from '../models/ShopExternalIdResponse';
import type { ShopResponse } from '../models/ShopResponse';
import type { TerminalExternalIdResponse } from '../models/TerminalExternalIdResponse';
import type { TerminalResponse } from '../models/TerminalResponse';
import type { WalletServiceAutocompleteResponse } from '../models/WalletServiceAutocompleteResponse';
import type { WalletUserExternalIdResponse } from '../models/WalletUserExternalIdResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AutocompleteRepository {

  /**
   * アクワイアラ自動補完
   * @returns AcquirerResponse OK
   * @throws ApiError
   */
  public static searchAcquirer({
    term,
  }: {
    /**
     * アクワイアラid又はアクワイアラ名で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<AcquirerResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/acquirer',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 一括付与自動補完
   * 最大100件まで取得する
   * @returns BulkGrantResponse OK
   * @throws ApiError
   */
  public static searchBulkGrant({
    term,
  }: {
    /**
     * 一括付与idで完全一致検索、又は一括付与管理名称で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<BulkGrantResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/bulk_grant',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン自動補完
   * 最大100件まで取得する
   * @returns CampaignResponse OK
   * @throws ApiError
   */
  public static searchCampaign({
    term,
  }: {
    /**
     * キャンペーンidで完全一致検索、又はキャンペーン管理名称で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<CampaignResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/campaign',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 企業自動補完
   * 最大100件まで取得する
   * @returns CompanyResponse OK
   * @throws ApiError
   */
  public static searchCompany({
    term,
    acquirerId,
  }: {
    /**
     * 企業id又は企業名で部分一致検索が可能
     */
    term: string | undefined,
    /**
     * アクワイアラid
     */
    acquirerId: string | undefined,
  }): CancelablePromise<Array<CompanyResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/company',
      query: {
        'term': term,
        'acquirerId': acquirerId,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 外部企業ID自動補完
   * 外部企業IDの部分一致検索を行う。最大100件まで取得する
   * @returns CompanyExternalIdResponse OK
   * @throws ApiError
   */
  public static searchCompanyExternalId({
    externalIdTerm,
  }: {
    /**
     * 外部企業IDで部分一致検索が可能
     */
    externalIdTerm: string | undefined,
  }): CancelablePromise<Array<CompanyExternalIdResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/company/externalId',
      query: {
        'externalIdTerm': externalIdTerm,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ファイル種別補完
   * @returns DownloadFileTypeResponse ログインユーザのロールに紐づくダウンロード可能なファイル種別を取得する
   * @throws ApiError
   */
  public static searchDownloadFileType(): CancelablePromise<Array<DownloadFileTypeResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/download_file_type',
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 商品券自動補完
   * @returns GiftAutocompleteResponse OK
   * @throws ApiError
   */
  public static searchGift({
    term,
  }: {
    /**
     * 商品券id又は商品件名で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<GiftAutocompleteResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/gift',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 商品券詳細自動補完
   * @returns GiftDetailAutocompleteResponse OK
   * @throws ApiError
   */
  public static searchGift1({
    term,
  }: {
    /**
     * 商品券詳細id又は商品券詳細名で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<GiftDetailAutocompleteResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/gift_detail',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 商品券セット自動補完
   * @returns GiftSetAutocompleteResponse OK
   * @throws ApiError
   */
  public static searchGiftSet({
    term,
  }: {
    /**
     * IDまたは商品券セット名で検索
     */
    term: string | undefined,
  }): CancelablePromise<Array<GiftSetAutocompleteResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/gift_set',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * イシュア自動補完
   * @returns IssuerResponse OK
   * @throws ApiError
   */
  public static searchIssuer({
    term,
  }: {
    /**
     * イシュアid又はイシュア名で部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<IssuerResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/issuer',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗自動補完
   * 最大100件まで取得する
   * @returns ShopResponse OK
   * @throws ApiError
   */
  public static searchShop({
    term,
    companyId,
  }: {
    /**
     * 店舗id又は店舗名で部分一致検索が可能
     */
    term: string | undefined,
    /**
     * 企業ID
     */
    companyId: string | undefined,
  }): CancelablePromise<Array<ShopResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/shop',
      query: {
        'term': term,
        'companyId': companyId,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 外部店舗ID自動補完
   * 外部店舗IDの部分一致検索を行う。最大100件まで取得する
   * @returns ShopExternalIdResponse OK
   * @throws ApiError
   */
  public static searchShopExternalId({
    externalIdTerm,
  }: {
    /**
     * 外部店舗IDで部分一致検索が可能
     */
    externalIdTerm: string | undefined,
  }): CancelablePromise<Array<ShopExternalIdResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/shop/externalId',
      query: {
        'externalIdTerm': externalIdTerm,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店頭端末自動補完
   * 最大100件まで取得する
   * @returns TerminalResponse OK
   * @throws ApiError
   */
  public static searchTerminal({
    term,
  }: {
    /**
     * 店頭端末idで部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<TerminalResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/terminal',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 外部端末ID自動補完
   * 外部端末IDの部分一致検索を行う。最大100件まで取得する
   * @returns TerminalExternalIdResponse OK
   * @throws ApiError
   */
  public static searchTerminalExternalId({
    externalIdTerm,
  }: {
    /**
     * 外部端末IDで部分一致検索が可能
     */
    externalIdTerm: string | undefined,
  }): CancelablePromise<Array<TerminalExternalIdResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/terminal/externalId',
      query: {
        'externalIdTerm': externalIdTerm,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットサービス自動補完
   * @returns WalletServiceAutocompleteResponse OK
   * @throws ApiError
   */
  public static searchWalletService({
    term,
    bankPay,
    issuerId,
  }: {
    /**
     * ウォレットサービスid又はウォレットサービス名で部分一致検索が可能
     */
    term: string | undefined,
    /**
     *
     * BankPayフラグ
     *
     * trueの時、term検索条件に加え、BankPayを利用するWalletServiceのみを絞り込んで取得する.
     * nullまたはfalseの時、term検索条件のみで絞り込んで検索する.
     *
     */
    bankPay: boolean | undefined,
    /**
     * イシュアid
     */
    issuerId: string | undefined,
  }): CancelablePromise<Array<WalletServiceAutocompleteResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/wallet_service',
      query: {
        'term': term,
        'bankPay': bankPay,
        'issuerId': issuerId,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 外部ウォレットユーザID自動補完
   * 外部ウォレットユーザIDの部分一致検索を行う。最大100件まで取得する
   * @returns WalletUserExternalIdResponse OK
   * @throws ApiError
   */
  public static searchWalletUserExternalId({
    term,
  }: {
    /**
     * 外部ウォレットユーザIDで部分一致検索が可能
     */
    term: string | undefined,
  }): CancelablePromise<Array<WalletUserExternalIdResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/autocomplete/wallet_user/externalId',
      query: {
        'term': term,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

}
